import React from 'react';
import { motion } from 'framer-motion';
import {
  Heart, Target, Shield, Zap, Eye, Award,
  Lightbulb, Handshake, TrendingUp, Bot,
} from 'lucide-react';
import PageHero from '../components/shared/PageHero';
import StatsBar from '../components/shared/StatsBar';
import CTABanner from '../components/shared/CTABanner';
import TechPattern from '../components/shared/TechPattern';

const values = [
  {
    icon: Target,
    title: 'Results Over Promises',
    description: 'We don\'t sell visions. We deliver measurable outcomes. Every agent comes with objective quality metrics: accuracy, speed, volume. You know exactly what you\'re getting.',
    color: '#4f6ef7',
  },
  {
    icon: Shield,
    title: 'Security First',
    description: 'Each company has a completely isolated environment. Data never mixes between clients. Full audit trails. Controlled permissions. Your data stays yours.',
    color: '#06d6a0',
  },
  {
    icon: Eye,
    title: 'Radical Transparency',
    description: 'If our product doesn\'t do something, we say so. We never promise a feature that doesn\'t exist. Credibility is worth more than any deal.',
    color: '#f59e0b',
  },
  {
    icon: Lightbulb,
    title: 'Continuous Innovation',
    description: 'Our agents evolve automatically. New capabilities, better performance, deeper learning. Your AI team gets better every day without you lifting a finger.',
    color: '#ec4899',
  },
  {
    icon: Handshake,
    title: 'Partnership, Not Dependency',
    description: 'We build systems that make your team stronger, not dependent on us. The knowledge stays on the platform, not with external people.',
    color: '#7c3aed',
  },
  {
    icon: TrendingUp,
    title: 'Compounding Intelligence',
    description: 'Every interaction makes the system smarter. Your AI team accumulates deep knowledge about your business that compounds over time.',
    color: '#06b6d4',
  },
];

const milestones = [
  { year: '2025', title: 'Founded', desc: 'Started with a vision: software engineers powered by AI, delivering at enterprise scale.', color: '#2563eb' },
  { year: '2025 Q3', title: 'First Clients', desc: 'Globo.com and LuizaLabs trusted us to transform their engineering operations.', color: '#0891b2' },
  { year: '2026 Q1', title: '50+ AI Agents', desc: 'Scaled our platform with engineers and AI agents working together across multiple industries.', color: '#ea580c' },
  { year: '2026+', title: 'Growing Fast', desc: 'Expanding to new industries and building the future of AI-powered engineering teams.', color: '#7c3aed' },
];

const AboutPage: React.FC = () => {
  return (
    <>
      <PageHero
        label="About AI Teams"
        labelIcon={<Heart size={14} />}
        title={
          <>
            We Build <span className="gradient-text">AI Teams</span>{' '}
            That Actually Work
          </>
        }
        subtitle="We're not another AI tool company. We deliver custom-built AI workforces that operate continuously inside your business, with real accountability and measurable results."
      />

      {/* Stats */}
      <section style={{ paddingBottom: 80 }}>
        <div className="container">
          <StatsBar />
        </div>
      </section>

      {/* Mission */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <span className="section-label">
              <Target size={14} />
              Our Mission
            </span>
            <h2 className="section-title" style={{ margin: '0 auto 24px' }}>
              Making AI <span className="gradient-text-cyan">Work For Everyone</span>
            </h2>
            <div style={{
              fontSize: '1.15rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              textAlign: 'left',
            }}>
              <p style={{ marginBottom: 20 }}>
                Today, getting real value from AI requires either a massive investment in consultants
                or a CTO willing to become a full-time AI system operator. Most mid-market companies
                are left behind.
              </p>
              <p style={{ marginBottom: 20 }}>
                We started AI Teams to change that. Our product turns months of configuration into
                minutes of onboarding. It turns expensive consulting projects into predictable SaaS
                subscriptions. It turns one-off AI experiments into continuous, measurable operations.
              </p>
              <motion.div
                style={{
                  padding: '20px 28px',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: 16,
                }}
              >
                <p style={{
                  fontSize: '1.1rem',
                  color: 'var(--accent-cyan)',
                  fontWeight: 600,
                  lineHeight: 1.6,
                  margin: 0,
                }}>
                  Other tools give AI to your team to use.
                  We give you an AI team to work alongside yours.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section" style={{ position: 'relative', overflow: 'hidden' }}>
        <TechPattern variant="hexagon" opacity={0.025} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <span className="section-label">
              <Award size={14} />
              Our Values
            </span>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              What <span className="gradient-text">Drives Us</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              The principles that guide every decision we make, from product design to client relationships.
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}>
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  whileHover={{ y: -6 }}
                  style={{
                    background: 'white',
                    borderRadius: 'var(--radius-lg)',
                    padding: '32px 24px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-card)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: value.color,
                  }} />
                  <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: `${value.color}0a`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                  }}>
                    <Icon size={26} color={value.color} />
                  </div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 10 }}>
                    {value.title}
                  </h3>
                  <p style={{
                    fontSize: '0.88rem',
                    color: 'var(--text-muted)',
                    lineHeight: 1.6,
                  }}>
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            section > .container > div[style*="grid-template-columns: repeat(3"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Timeline */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <span className="section-label">
              <Zap size={14} />
              Our Journey
            </span>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              Building the <span className="gradient-text-cyan">Future of Work</span>
            </h2>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 20,
            maxWidth: 1000,
            margin: '0 auto',
          }}>
            {milestones.map((milestone, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                style={{
                  background: 'white',
                  borderRadius: 'var(--radius)',
                  padding: '28px 20px',
                  border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: milestone.color,
                }} />
                <div style={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: milestone.color,
                  marginBottom: 12,
                  fontFamily: 'Space Grotesk',
                }}>
                  {milestone.year}
                </div>
                <h4 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 8 }}>
                  {milestone.title}
                </h4>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {milestone.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            section > .container > div[style*="grid-template-columns: repeat(4"] {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
        `}</style>
      </section>

      {/* Agents count highlight */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-lg)',
              padding: '64px 48px',
              border: '1px solid var(--border-color)',
              textAlign: 'center',
            }}
          >
            <Bot size={40} color="var(--accent-blue)" style={{ marginBottom: 16 }} />
            <div style={{
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              fontWeight: 900,
              fontFamily: 'Space Grotesk',
              background: 'var(--gradient-cyan)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1,
              marginBottom: 12,
            }}>
              50+
            </div>
            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 8 }}>
              AI Agents Currently Active
            </h3>
            <p style={{
              fontSize: '1rem',
              color: 'var(--text-secondary)',
              maxWidth: 500,
              margin: '0 auto',
              lineHeight: 1.6,
            }}>
              Operating 24/7, executing tasks daily with 98.5% accuracy
              across multiple industries.
            </p>
          </motion.div>
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default AboutPage;
